'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { FvdButton } from '@/components/design/FvdControls';
import { MyFitventLockup } from '@/components/design/FitventMark';
import { HubNotice } from '../hub-ui';
import { downloadMyData } from './download-data';
import { formatDeletionDate } from '@/lib/account-deletion/window';

/**
 * What an athlete sees on signing in while their account is waiting to be
 * deleted, in place of the whole hub (HubShell draws nothing else): the date,
 * "Keep my account", "Download my data", and a way to sign out.
 *
 * Keep stays until the erasure has actually happened, after the date too:
 * the daily cron claims the account under the same row lock Keep needs, so
 * a Keep that wins is a real keep.
 */
export function DeletionPendingScreen({ scheduledFor, now = Date.now() }: { scheduledFor: string; now?: number }) {
  const router = useRouter();
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const date = formatDeletionDate(scheduledFor);
  const due = new Date(scheduledFor).getTime() <= now;

  const keep = async () => {
    setKeeping(true);
    setKeepError('');
    try {
      const res = await fetch('/api/participant/hub/profile/deletion', { method: 'DELETE' });
      if (!res.ok) throw new Error('keep failed');
      // The layout reads the account again and draws the hub.
      router.refresh();
    } catch {
      setKeepError('We couldn’t keep your account just now. Try again in a moment.');
      setKeeping(false);
    }
  };

  const download = async () => {
    setExporting(true);
    setExportMessage(await downloadMyData());
    setExporting(false);
  };

  const signOut = async () => {
    setSigningOut(true);
    try {
      await fetch('/api/participant/auth/logout', { method: 'POST' });
    } finally {
      router.push('/hub/login');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--fvd-canvas)',
        color: 'var(--fvd-ink)',
        fontFamily: 'var(--fvd-font-body)',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <header className="flex items-center px-4 py-4 md:px-7 md:py-5" style={{ gap: 12 }}>
        <MyFitventLockup markSize={17} />
        <span style={{ flex: 1 }} />
        {/* 44px tall at every width: a real tap target, not the 29px small button. */}
        <button
          type="button"
          onClick={signOut}
          disabled={signingOut}
          className="fvd-hoverable"
          style={{
            minHeight: 44,
            padding: '0 14px',
            border: '1px solid var(--fvd-border-control)',
            borderRadius: 9,
            background: 'var(--fvd-canvas)',
            color: 'var(--fvd-ink)',
            fontFamily: 'inherit',
            fontSize: 13.5,
            fontWeight: 500,
            cursor: signingOut ? 'default' : 'pointer',
            opacity: signingOut ? 0.6 : 1,
          }}
        >
          {signingOut ? 'Signing out' : 'Sign out'}
        </button>
      </header>
      <main className="px-4 pt-8 pb-12 md:pt-16" style={{ flex: 1 }}>
        <div style={{ maxWidth: 460, margin: '0 auto' }}>
          <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.25, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {due ? 'Your account is about to be deleted' : `Your account is due to be deleted on ${date}`}
          </h1>
          <p style={{ margin: '12px 0 0', fontSize: 14.5, lineHeight: 1.6, color: 'var(--fvd-ink-secondary)' }}>
            {due
              ? 'The 30 days are up and it will be deleted soon. Changed your mind? You can still keep it until then.'
              : 'You asked us to delete your MyFitvent account. Changed your mind? Keep it and everything carries on as before.'}
          </p>
          <div className="flex flex-col sm:flex-row" style={{ gap: 10, marginTop: 24 }}>
            <FvdButton variant="primary" onClick={keep} busy={keeping}>
              {keeping ? 'Keeping your account' : 'Keep my account'}
            </FvdButton>
            <FvdButton onClick={download} disabled={exporting}>{exporting ? 'Preparing' : 'Download my data'}</FvdButton>
          </div>
          {keepError && <div style={{ marginTop: 12 }}><HubNotice tone="danger">{keepError}</HubNotice></div>}
          {exportMessage && <div style={{ marginTop: 12 }}><HubNotice tone={exportMessage.tone}>{exportMessage.text}</HubNotice></div>}
        </div>
      </main>
    </div>
  );
}
